import React from 'react'
import styled from 'styled-components'

import type {NavatticEvent as SegmentNavatticEvent} from '@/analytics/segment'
import {navatticEvent} from '@/analytics/segment'
import {useSignupRedirect} from '@/hooks/use-signup-redirect'
import {useLocalStorage} from '@/hooks/use-storage'
import type {NavatticEvent} from '@/types/navattic-event'

type NavatticDemoProps = {
    title: string
    src: string
    tabIndex?: number
    style?: React.CSSProperties
    onIFrameReady?: () => void
}

enum FlowProgress {
    STARTED = 'started',
    PROGRESSING = 'progressing',
    COMPLETED = 'completed',
}

export type Completions =
    | {
          flowProgress: FlowProgress
          completions: string
      }
    | Record<PropertyKey, never>

const NavatticDemo = ({onIFrameReady, ...rest}: NavatticDemoProps) => {
    const [isRedirecting, setIsRedirecting] = React.useState<boolean>(false)
    const [redirectUrl, setRedirectUrl] = React.useState<string>('')
    const [navatticDemoCompletion, setNavatticDemoCompletion] = useLocalStorage<Completions>(
        'navatticDemoCompletion',
        {},
    )
    const [completedFlow, setCompletedFlow] = React.useState(false)
    const [progresingFlow, setProgresingFlow] = React.useState(false)
    const [completions, setCompletions] = React.useState('')

    const redirectPath = useSignupRedirect({
        redirectPath: redirectUrl,
        params: {
            navattic_flows_completed: navatticDemoCompletion.completions,
            navattic_flow_progress: navatticDemoCompletion.flowProgress,
        },
    })

    React.useEffect(() => {
        const handleNavatticEvents = (event: MessageEvent<NavatticEvent>) => {
            if (event.origin !== 'https://pleo.navattic.com') {
                return
            }

            if (onIFrameReady && event.data.kind === 'navattic:stylesheetsLoaded') {
                onIFrameReady()
            }

            if (event.data.kind === 'navattic:event') {
                const eventData = event.data as SegmentNavatticEvent
                navatticEvent(eventData)

                // Navattic may fire a VIEW_STEP together with the START_FLOW event
                // so we check the step index to ensure we are not on the first step.
                if (eventData.event === 'VIEW_STEP' && event.data.step.indx > 0) {
                    setProgresingFlow(true)
                }

                // Navattic fires a VIEW_STEP right after the COMPLETE_FLOW event
                // so we need to keep an internal state for the completedFlow.
                if (eventData.event === 'COMPLETE_FLOW') {
                    setCompletedFlow(true)
                }

                // Navattic keeps its own state for the completions so we tap into that.
                setCompletions((event.data.completions || []).length.toString())

                if (event.data.event === 'NAVIGATE') {
                    setIsRedirecting(true)
                    setRedirectUrl(event.data.url)
                }
            }
        }

        window.addEventListener('message', handleNavatticEvents)

        return () => window.removeEventListener('message', handleNavatticEvents)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    React.useEffect(() => {
        // We keep track of flow completions and the flow progress.
        // The flowProgess gets updated until any flow gets completed for the first time.
        let flowProgress = navatticDemoCompletion.flowProgress

        if (flowProgress !== FlowProgress.COMPLETED) {
            if (completedFlow) {
                flowProgress = FlowProgress.COMPLETED
            } else {
                if (progresingFlow) {
                    flowProgress = FlowProgress.PROGRESSING
                } else {
                    if (!flowProgress) {
                        flowProgress = FlowProgress.STARTED
                    }
                }
            }
        }

        setNavatticDemoCompletion({
            completions,
            flowProgress,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [completedFlow, progresingFlow, completions, navatticDemoCompletion.flowProgress])

    React.useEffect(() => {
        if (isRedirecting) {
            if (redirectUrl.endsWith('/register')) {
                window.location.assign(redirectPath)
            } else {
                window.location.assign(redirectUrl)
            }

            setIsRedirecting(false)
        }
    }, [redirectPath, isRedirecting, redirectUrl])

    return !isRedirecting ? <StyledIFrame {...rest} /> : null
}

const StyledIFrame = styled.iframe`
    width: 100%;
    height: 100%;
    border: none;
`

export default NavatticDemo
